import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import styles from "./Popover.module.css";

interface PopoverProps {
  ancora: RefObject<HTMLElement>;
  aberto: boolean;
  onFechar: () => void;
  children: ReactNode;
  /** "conteudo" pode ampliar a largura da âncora conforme os itens; número usa px fixos. */
  largura?: number | "ancora" | "conteudo";
  alinhamento?: "inicio" | "fim";
  /** "lateral" abre ao lado da âncora (útil na sidebar, para não cobri-la). */
  posicao?: "vertical" | "lateral";
  /** Mantém este popup aberto ao interagir com outro Popover usado como submenu. */
  manterAbertoAoInteragirComOutroPopover?: boolean;
  /** Dist?ncia m?nima entre o popup e as bordas vis?veis da janela. */
  margemViewport?: number;
}

interface Posicao {
  /** Usado no modo vertical; no lateral quem manda é `bottom`. */
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight?: number;
}

/**
 * Popup posicionado em PORTAL (document.body) com position: fixed calculado a
 * partir do retângulo da âncora. Por estar fora do fluxo do modal, NUNCA é
 * cortado pelo overflow do modal nem abre por baixo do menu lateral (regra 5).
 * Reposiciona em scroll/resize e fecha no clique externo / ESC.
 */
export function Popover({
  ancora,
  aberto,
  onFechar,
  children,
  largura = "ancora",
  alinhamento = "inicio",
  posicao = "vertical",
  manterAbertoAoInteragirComOutroPopover = false,
  margemViewport = 8,
}: PopoverProps) {
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Posicao | null>(null);

  function recalcular() {
    const a = ancora.current;
    if (!a) return;
    const r = a.getBoundingClientRect();
    const pop = popRef.current;
    let larguraSolicitada = typeof largura === "number" ? largura : r.width;
    if (largura === "conteudo" && pop) {
      // Mede a largura natural antes de limitar o popup; o campo fechado mantém seu tamanho.
      pop.style.width = "max-content";
      larguraSolicitada = Math.max(r.width, Math.min(pop.getBoundingClientRect().width, 640));
    }
    const larguraPx = Math.min(larguraSolicitada, window.innerWidth - margemViewport * 2);

    /* A largura entra ANTES de medir a altura: sem ela o popup ainda está com a
       largura natural do conteúdo, o texto quebra diferente e a altura medida
       não é a final — o popup abria deslocado. */
    if (pop) pop.style.width = `${larguraPx}px`;
    const altura = pop?.offsetHeight ?? 300;

    /* Lateral: encosta na borda direita da âncora e alinha o rodapé dos dois,
       então o popup cresce para cima sem tampar o menu. Se não couber à
       direita, cai para o outro lado. */
    if (posicao === "lateral") {
      let left = r.right + 8;
      if (left + larguraPx > window.innerWidth - margemViewport) {
        left = Math.max(margemViewport, r.left - larguraPx - margemViewport);
      }
      /* Preso pelo RODAPÉ na base da âncora, não por um `top` calculado a partir
         da altura: assim o lugar de abertura é sempre o mesmo e o popup cresce
         para cima, tenha ele os itens que tiver (Agentes IA, Minha conta, aviso
         de trial). Com `top` derivado da altura, cada variação de conteúdo
         mudava a posição na tela. */
      setPos({
        bottom: Math.max(margemViewport, window.innerHeight - r.bottom),
        left,
        width: larguraPx,
        /* Teto de segurança: popup mais alto que o espaço disponível rola por
           dentro em vez de escapar pelo topo da janela. */
        maxHeight: Math.max(160, r.bottom - margemViewport * 2),
      });
      return;
    }

    const espacoAbaixo = window.innerHeight - r.bottom;
    const abrirAcima = espacoAbaixo < altura + 12 && r.top > espacoAbaixo;
    let left = alinhamento === "fim" ? r.right - larguraPx : r.left;
    left = Math.max(margemViewport, Math.min(left, window.innerWidth - larguraPx - margemViewport));
    const top = abrirAcima
      ? Math.max(margemViewport, r.top - altura - 6)
      : Math.min(r.bottom + 6, window.innerHeight - altura - margemViewport);
    setPos({ top, left, width: larguraPx });
  }

  useLayoutEffect(() => {
    if (aberto) recalcular();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, largura, children]);

  useEffect(() => {
    if (!aberto) return;
    const reposicionar = () => recalcular();
    const aoClicar = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (popRef.current?.contains(alvo) || ancora.current?.contains(alvo)) return;
      const elemento = alvo instanceof Element ? alvo : alvo.parentElement;
      if (manterAbertoAoInteragirComOutroPopover && elemento?.closest('[data-popover-raiz="true"]')) return;
      onFechar();
    };
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === "Escape") onFechar();
    };
    window.addEventListener("scroll", reposicionar, true);
    window.addEventListener("resize", reposicionar);
    document.addEventListener("mousedown", aoClicar);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      window.removeEventListener("scroll", reposicionar, true);
      window.removeEventListener("resize", reposicionar);
      document.removeEventListener("mousedown", aoClicar);
      document.removeEventListener("keydown", aoTeclar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, largura]);

  if (!aberto) return null;

  return createPortal(
    <div
      ref={popRef}
      data-popover-raiz="true"
      /* data-vidro: superfície que recebe o efeito Glass quando ligado. */
      data-vidro="true"
      className={styles.popover}
      onMouseDown={(evento) => evento.stopPropagation()}
      onClick={(evento) => evento.stopPropagation()}
      style={
        pos
          ? {
              top: pos.top,
              bottom: pos.bottom,
              left: pos.left,
              width: pos.width,
              maxHeight: pos.maxHeight,
              overflowY: pos.maxHeight != null ? "auto" : undefined,
            }
          : /* Primeiro quadro: fora da tela até a posição estar medida. */
            { top: -9999, left: -9999 }
      }
    >
      {children}
    </div>,
    document.body
  );
}
